import apiClient, { UPLOAD_REQUEST_TIMEOUT_MS } from "./apiClient";
import {
  DIRECT_EEG_UPLOAD_THRESHOLD_BYTES,
  PATIENT_METADATA_MISMATCH_CODE,
} from "./eegUploadConstants";
import type {
  DirectEegUploadResult,
  PatientMetadataMismatchDetails,
} from "./directEegUploadClient";
import {
  fileMatchesResumableSession,
  getPersistedResumableUploadPauseUntil,
  removePersistedResumableUploadSession,
  removePersistedResumableUploadSessionsMatching,
  upsertPersistedResumableUploadSession,
  type PersistedResumableEegUploadSession,
} from "./resumableEegUploadSessionStore";
import {
  hashEegUploadHeader,
  shouldSkipEegChunkUpload,
} from "./eegUploadContentIdentity";
import { getEegUploadCapabilities } from "./eegUploadCapabilitiesClient";
import { resolveEegUploadApiBaseUrl } from "./eegUploadOrigin";

const MIN_RESUMABLE_UPLOAD_CONCURRENCY = 16;
const MAX_RESUMABLE_UPLOAD_CONCURRENCY = 16;
const CHUNK_UPLOAD_RETRIES = 3;
const MIB = 1024 * 1024;
const CHUNK_SIZE_25_MIB = 25 * MIB;
const CHUNK_SIZE_50_MIB = 50 * MIB;

interface ResumableUploadInitPayload {
  uploadId: string;
  recordingId: string;
  chunkSize: number;
  totalChunks: number;
  reused?: boolean;
  reuse_reason?: string;
  staged_upload_id?: string;
  filename?: string;
  size_bytes?: number;
}

export interface ResumableUploadStatusPayload {
  uploadId?: string;
  recordingId?: string;
  status?: "uploading" | "completed";
  uploadedChunks: number;
  totalChunks: number;
  progress: number;
  complete: boolean;
  missingChunkIndices?: number[];
}

interface ResumableUploadOptions {
  studyId: string;
  file: File;
  owningTeamId?: string | null;
  patientId?: string;
  importTaskId?: string;
  onProgress?: (progressPercent: number) => void;
  onTelemetry?: (telemetry: ResumableUploadTelemetry) => void;
  confirmPatientMetadataMismatch?: (
    filename: string,
    details: PatientMetadataMismatchDetails,
  ) => Promise<boolean>;
}

interface ResumableSessionFileUploadOptions {
  studyId: string;
  file: File;
  relativePath?: string;
  fieldname: "eegFiles" | "tevFiles" | "edfEventIdFiles" | "tvsFiles" | "videoFiles";
  owningTeamId?: string | null;
  importTaskId?: string;
  onProgress?: (progressPercent: number) => void;
  onTelemetry?: (telemetry: ResumableUploadTelemetry) => void;
}

export interface ResumableUploadTelemetry {
  uploadedBytes: number;
  totalBytes: number;
  averageBytesPerSecond: number;
  currentBytesPerSecond: number;
  etaSeconds: number | null;
  completedChunks: number;
  totalChunks: number;
  activeConcurrency: number;
  retryCount: number;
  chunkSize: number;
}

type UploadChunkResult = {
  attempts: number;
};

type UploadTelemetryTracker = {
  startedAt: number;
  lastSampleAt: number;
  lastUploadedBytes: number;
  retryCount: number;
};

export interface StagedSessionFileUpload {
  staged_upload_id: string;
  filename: string;
  size_bytes: number;
  fieldname: ResumableSessionFileUploadOptions["fieldname"];
  relative_path: string;
}

export interface ResumeEegResumableOptions extends ResumableUploadOptions {
  session: PersistedResumableEegUploadSession;
}

export type ResumableUploadProbeResult =
  | {
      state: "resumable";
      uploadId: string;
      recordingId?: string;
      uploadedChunks: number;
      totalChunks: number;
      progress: number;
      complete: boolean;
      missingChunkIndices: number[];
    }
  | {
      state: "completed";
      uploadId: string;
      recordingId: string;
    }
  | {
      state: "dead";
      uploadId: string;
      reason: string;
    };

interface ApiErrorPayload {
  code?: string;
  message?: string;
  details?: PatientMetadataMismatchDetails;
}

const wait = (ms: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const waitIfUploadPaused = async (uploadId: string): Promise<void> => {
  while (true) {
    const pausedUntil = getPersistedResumableUploadPauseUntil(uploadId);
    if (pausedUntil === undefined) return;
    if (pausedUntil === null) {
      await wait(30_000);
      continue;
    }
    const remainingMs = pausedUntil - Date.now();
    if (remainingMs <= 0) return;
    await wait(Math.min(remainingMs, 30_000));
  }
};

function chooseRequestedChunkSize(fileSizeBytes: number): number {
  if (fileSizeBytes >= 4 * 1024 * MIB) {
    return CHUNK_SIZE_50_MIB;
  }
  if (fileSizeBytes >= 512 * MIB) {
    return CHUNK_SIZE_25_MIB;
  }
  return 10 * MIB;
}

async function buildEegUploadRequestConfig(headerSha256?: string): Promise<{
  timeout: number;
  baseURL?: string;
  headers: Record<string, string>;
}> {
  const capabilities = await getEegUploadCapabilities();
  const pageHostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const resolvedBase = resolveEegUploadApiBaseUrl({
    defaultBaseUrl: String(apiClient.defaults.baseURL || "/api"),
    publicUploadOrigin: capabilities.public_upload_origin,
    pageHostname,
  });
  const envOverride = String(
    import.meta.env.VITE_EEG_UPLOAD_API_URL || "",
  ).trim();
  const headers: Record<string, string> = {};
  if (headerSha256) {
    headers["X-NerveDX-EEG-Header-SHA256"] = headerSha256;
  }
  return {
    timeout: UPLOAD_REQUEST_TIMEOUT_MS,
    baseURL: envOverride || resolvedBase,
    headers,
  };
}

function createTelemetryTracker(): UploadTelemetryTracker {
  const now = Date.now();
  return {
    startedAt: now,
    lastSampleAt: now,
    lastUploadedBytes: 0,
    retryCount: 0,
  };
}

function emitUploadTelemetry(args: {
  tracker: UploadTelemetryTracker;
  uploadedChunks: number;
  totalChunks: number;
  chunkSize: number;
  totalBytes: number;
  activeConcurrency: number;
  onTelemetry?: (telemetry: ResumableUploadTelemetry) => void;
}) {
  if (!args.onTelemetry) return;
  const now = Date.now();
  const elapsedSeconds = Math.max((now - args.tracker.startedAt) / 1000, 0.001);
  const sampleSeconds = Math.max((now - args.tracker.lastSampleAt) / 1000, 0.001);
  const uploadedBytes = Math.min(
    args.totalBytes,
    args.uploadedChunks * args.chunkSize,
  );
  const averageBytesPerSecond = uploadedBytes / elapsedSeconds;
  const currentBytesPerSecond =
    (uploadedBytes - args.tracker.lastUploadedBytes) / sampleSeconds;
  const remainingBytes = Math.max(args.totalBytes - uploadedBytes, 0);
  const etaSeconds =
    averageBytesPerSecond > 0 ? remainingBytes / averageBytesPerSecond : null;

  args.tracker.lastSampleAt = now;
  args.tracker.lastUploadedBytes = uploadedBytes;
  args.onTelemetry({
    uploadedBytes,
    totalBytes: args.totalBytes,
    averageBytesPerSecond: Math.round(averageBytesPerSecond),
    currentBytesPerSecond: Math.max(0, Math.round(currentBytesPerSecond)),
    etaSeconds: etaSeconds === null ? null : Math.round(etaSeconds),
    completedChunks: args.uploadedChunks,
    totalChunks: args.totalChunks,
    activeConcurrency: args.activeConcurrency,
    retryCount: args.tracker.retryCount,
    chunkSize: args.chunkSize,
  });
}

const uploadChunkWithRetry = async (
  uploadId: string,
  chunkIndex: number,
  blob: Blob,
  requestConfig?: { timeout?: number; baseURL?: string },
): Promise<UploadChunkResult> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < CHUNK_UPLOAD_RETRIES; attempt += 1) {
    try {
      const formData = new FormData();
      formData.append("chunk", blob, `chunk_${chunkIndex}`);
      formData.append("chunkIndex", String(chunkIndex));
      await apiClient.post(`/eeg/upload/${uploadId}/chunk`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: requestConfig?.timeout,
        baseURL: requestConfig?.baseURL,
      });
      return { attempts: attempt + 1 };
    } catch (error) {
      lastError = error;
      await wait(500 * (attempt + 1));
    }
  }
  throw lastError;
};

export async function getResumableUploadStatus(
  uploadId: string,
): Promise<ResumableUploadStatusPayload> {
  const response = await apiClient.get<{ data?: ResumableUploadStatusPayload }>(
    `/eeg/upload/${uploadId}/status`,
  );
  const status = response.data?.data;
  if (!status?.totalChunks) {
    throw new Error("Resumable EEG upload status did not return a valid session.");
  }
  return status;
}

export async function probeResumableUploadSession(
  uploadId: string,
): Promise<ResumableUploadProbeResult> {
  try {
    const status = await getResumableUploadStatus(uploadId);
    if (status.status === "completed" && status.recordingId) {
      return {
        state: "completed",
        uploadId,
        recordingId: status.recordingId,
      };
    }
    return {
      state: "resumable",
      uploadId,
      recordingId: status.recordingId,
      uploadedChunks: status.uploadedChunks,
      totalChunks: status.totalChunks,
      progress: status.progress,
      complete: status.complete,
      missingChunkIndices: status.missingChunkIndices || [],
    };
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number; data?: { error?: string } } })
      ?.response?.status;
    const message =
      (error as { response?: { data?: { error?: string } } })?.response?.data
        ?.error ||
      (error instanceof Error ? error.message : "Upload session is not available.");
    if (status === 404 || status === 403) {
      return { state: "dead", uploadId, reason: message };
    }
    throw error;
  }
}

const ensureMissingChunksUploaded = async ({
  uploadId,
  file,
  chunkSize,
  totalChunks,
  onProgress,
  onTelemetry,
  requestConfig,
}: {
  uploadId: string;
  file: File;
  chunkSize: number;
  totalChunks: number;
  onProgress?: (progressPercent: number) => void;
  onTelemetry?: (telemetry: ResumableUploadTelemetry) => void;
  requestConfig?: { timeout?: number; baseURL?: string };
}) => {
  const status = await getResumableUploadStatus(uploadId);
  if (status.complete) {
    onProgress?.(
      Math.min(99, Math.round((status.uploadedChunks * 100) / totalChunks)),
    );
    return;
  }

  const missing = Array.isArray(status.missingChunkIndices)
    ? [...status.missingChunkIndices]
    : [];
  if (missing.length === 0) {
    throw new Error(
      `Upload is incomplete: ${status.totalChunks - status.uploadedChunks} chunks still missing.`,
    );
  }

  const telemetryTracker = createTelemetryTracker();
  let completedRepairs = 0;
  for (const chunkIndex of missing) {
    const start = chunkIndex * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const result = await uploadChunkWithRetry(
      uploadId,
      chunkIndex,
      file.slice(start, end),
      requestConfig,
    );
    telemetryTracker.retryCount += Math.max(0, result.attempts - 1);
    completedRepairs += 1;
    emitUploadTelemetry({
      tracker: telemetryTracker,
      uploadedChunks: totalChunks - missing.length + completedRepairs,
      totalChunks,
      chunkSize,
      totalBytes: file.size,
      activeConcurrency: 1,
      onTelemetry,
    });
  }

  const refreshed = await getResumableUploadStatus(uploadId);
  onProgress?.(
    Math.min(
      99,
      Math.round((refreshed.uploadedChunks * 100) / Math.max(totalChunks, 1)),
    ),
  );

  if (!refreshed.complete) {
    throw new Error(
      `Upload is incomplete: ${refreshed.missingChunkIndices?.length || refreshed.totalChunks - refreshed.uploadedChunks} chunks still missing.`,
    );
  }
};

const uploadRemainingChunks = async ({
  uploadId,
  file,
  chunkSize,
  totalChunks,
  onProgress,
  onTelemetry,
}: {
  uploadId: string;
  file: File;
  chunkSize: number;
  totalChunks: number;
  onProgress?: (progressPercent: number) => void;
  onTelemetry?: (telemetry: ResumableUploadTelemetry) => void;
}) => {
  const initialStatus = await getResumableUploadStatus(uploadId);
  if (initialStatus.complete) {
    onProgress?.(
      Math.min(
        99,
        Math.round((initialStatus.uploadedChunks * 100) / Math.max(totalChunks, 1)),
      ),
    );
    return;
  }

  const missing = Array.isArray(initialStatus.missingChunkIndices)
    ? [...initialStatus.missingChunkIndices]
    : [];
  if (missing.length === 0) {
    throw new Error(
      `Upload is incomplete: ${initialStatus.totalChunks - initialStatus.uploadedChunks} chunks still missing.`,
    );
  }

  const reportProgress = (completedMissingChunks: number) => {
    onProgress?.(
      Math.min(
        99,
        Math.round(
          ((totalChunks - missing.length + completedMissingChunks) * 100) /
            Math.max(totalChunks, 1),
        ),
      ),
    );
  };

  let nextIndex = 0;
  let activeConcurrency = Math.min(
    MIN_RESUMABLE_UPLOAD_CONCURRENCY,
    missing.length,
  );
  const telemetryTracker = createTelemetryTracker();
  let completedMissingChunks = 0;
  const workers = Array.from({
    length: Math.min(MAX_RESUMABLE_UPLOAD_CONCURRENCY, missing.length),
  }).map(async (_unused, workerIndex) => {
    while (nextIndex < missing.length) {
      if (workerIndex >= activeConcurrency) {
        await wait(250);
        continue;
      }
      const chunkIndex = missing[nextIndex];
      nextIndex += 1;
      await waitIfUploadPaused(uploadId);
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const startedAt = Date.now();
      const result = await uploadChunkWithRetry(
        uploadId,
        chunkIndex,
        file.slice(start, end),
      );
      telemetryTracker.retryCount += Math.max(0, result.attempts - 1);
      if (
        Date.now() - startedAt < 8_000 &&
        activeConcurrency < MAX_RESUMABLE_UPLOAD_CONCURRENCY
      ) {
        activeConcurrency += 1;
      }
      completedMissingChunks += 1;
      reportProgress(completedMissingChunks);
      emitUploadTelemetry({
        tracker: telemetryTracker,
        uploadedChunks: totalChunks - missing.length + completedMissingChunks,
        totalChunks,
        chunkSize,
        totalBytes: file.size,
        activeConcurrency,
        onTelemetry,
      });
    }
  });
  await Promise.all(workers);

  await ensureMissingChunksUploaded({
    uploadId,
    file,
    chunkSize,
    totalChunks,
    onProgress,
    onTelemetry,
  });
};

const persistActiveSession = (
  session: PersistedResumableEegUploadSession,
): void => {
  upsertPersistedResumableUploadSession(session);
};

const clearPersistedSession = (uploadId: string): void => {
  removePersistedResumableUploadSession(uploadId);
};

const finalizeResumableUpload = async ({
  uploadId,
  allowPatientMismatchOverride,
  onProgress,
}: {
  uploadId: string;
  allowPatientMismatchOverride: boolean;
  onProgress?: (progressPercent: number) => void;
}): Promise<DirectEegUploadResult> => {
  const completeResponse = await apiClient.post<{
    data: {
      recording_id: string;
      start_datetime?: string | null;
      duration_seconds?: number | null;
    };
  }>(
    `/eeg/upload/${uploadId}/complete`,
    {
      patient_mismatch_override: allowPatientMismatchOverride ? "true" : undefined,
    },
    { timeout: UPLOAD_REQUEST_TIMEOUT_MS },
  );

  const recording = completeResponse.data?.data;
  if (!recording?.recording_id) {
    throw new Error("Resumable EEG upload completed without recording_id.");
  }

  clearPersistedSession(uploadId);
  onProgress?.(100);
  return {
    recordingId: recording.recording_id,
    startDatetime: recording.start_datetime || null,
    logicalDurationSeconds: recording.duration_seconds ?? null,
    segmentCount: 1,
  };
};

const runResumableUploadWithMismatchHandling = async (
  run: (allowPatientMismatchOverride: boolean) => Promise<DirectEegUploadResult>,
  options: ResumableUploadOptions,
): Promise<DirectEegUploadResult> => {
  try {
    return await run(false);
  } catch (error: unknown) {
    const payload = (error as { response?: { data?: ApiErrorPayload; status?: number } })
      ?.response?.data;
    if (
      (error as { response?: { status?: number } })?.response?.status === 409 &&
      payload?.code === PATIENT_METADATA_MISMATCH_CODE &&
      payload.details?.override_allowed
    ) {
      const confirmMismatch =
        options.confirmPatientMetadataMismatch ||
        (async () => window.confirm("Patient metadata mismatch. Upload anyway?"));
      const confirmed = await confirmMismatch(options.file.name, payload.details);
      if (!confirmed) {
        throw new Error("Patient metadata override declined by user.");
      }
      return run(true);
    }
    throw error;
  }
};

export async function resumeEegResumable(
  options: ResumeEegResumableOptions,
): Promise<DirectEegUploadResult> {
  if (!fileMatchesResumableSession(options.file, options.session)) {
    throw new Error(
      `Selected file must match "${options.session.filename}" (${options.session.filesize} bytes).`,
    );
  }

  const probe = await probeResumableUploadSession(options.session.uploadId);
  if (probe.state === "dead") {
    clearPersistedSession(options.session.uploadId);
    throw new Error(probe.reason);
  }
  if (probe.state === "completed") {
    clearPersistedSession(options.session.uploadId);
    return {
      recordingId: probe.recordingId,
      startDatetime: null,
      logicalDurationSeconds: null,
      segmentCount: 1,
    };
  }

  persistActiveSession({
    ...options.session,
    updatedAt: Date.now(),
    importTaskId: options.importTaskId || options.session.importTaskId,
    patientId: options.patientId || options.session.patientId,
  });

  await uploadRemainingChunks({
    uploadId: options.session.uploadId,
    file: options.file,
    chunkSize: options.session.chunkSize,
    totalChunks: options.session.totalChunks,
    onProgress: options.onProgress,
  });

  return runResumableUploadWithMismatchHandling(
    async (allowOverride) =>
      finalizeResumableUpload({
        uploadId: options.session.uploadId,
        allowPatientMismatchOverride: allowOverride,
        onProgress: options.onProgress,
      }),
    options,
  );
}

export async function uploadEegResumable(
  options: ResumableUploadOptions,
): Promise<DirectEegUploadResult> {
  // Init once. Patient-mismatch is enforced on /complete; retrying override must
  // only re-call finalize — re-init left orphan sessions at 100% chunks uploaded
  // and caused "Upload paused at 100% — resume available" after a successful override.
  const identity = await hashEegUploadHeader(options.file);
  options.onTelemetry?.({
    uploadedBytes: 0,
    totalBytes: options.file.size,
    averageBytesPerSecond: 0,
    currentBytesPerSecond: 0,
    etaSeconds: null,
    completedChunks: 0,
    totalChunks: 0,
    activeConcurrency: 0,
    retryCount: 0,
    chunkSize: chooseRequestedChunkSize(options.file.size),
  });
  const uploadConfig = await buildEegUploadRequestConfig(identity.headerSha256);
  const initResponse = await apiClient.post<{
    data: ResumableUploadInitPayload;
  }>(
    "/eeg/upload/init",
    {
      study_id: options.studyId,
      filename: options.file.name,
      filesize: options.file.size,
      size_bytes: options.file.size,
      header_sha256: identity.headerSha256,
      chunk_size_bytes: chooseRequestedChunkSize(options.file.size),
      owning_team_id: options.owningTeamId || undefined,
    },
    uploadConfig,
  );

  const session = initResponse.data?.data;
  if (!session?.uploadId || !session.chunkSize || !session.totalChunks) {
    throw new Error("Resumable EEG upload did not return a valid session.");
  }

  removePersistedResumableUploadSessionsMatching({
    importTaskId: options.importTaskId,
    filename: options.file.name,
    studyId: options.studyId,
    exceptUploadId: session.uploadId,
  });

  const chunkSize = session.chunkSize;
  const totalChunks = session.totalChunks;
  const persistedSession: PersistedResumableEegUploadSession = {
    uploadId: session.uploadId,
    recordingId: session.recordingId,
    filename: options.file.name,
    filesize: options.file.size,
    studyId: options.studyId,
    chunkSize,
    totalChunks,
    patientId: options.patientId,
    owningTeamId: options.owningTeamId || null,
    importTaskId: options.importTaskId,
    startedAt: Date.now(),
    updatedAt: Date.now(),
  };
  persistActiveSession(persistedSession);

  const uploadedChunks = new Set<number>();
  const telemetryTracker = createTelemetryTracker();
  let activeConcurrency = Math.min(
    MIN_RESUMABLE_UPLOAD_CONCURRENCY,
    totalChunks,
  );
  const reportProgress = () => {
    options.onProgress?.(
      Math.min(
        99,
        Math.round((uploadedChunks.size * 100) / Math.max(totalChunks, 1)),
      ),
    );
    emitUploadTelemetry({
      tracker: telemetryTracker,
      uploadedChunks: uploadedChunks.size,
      totalChunks,
      chunkSize,
      totalBytes: options.file.size,
      activeConcurrency,
      onTelemetry: options.onTelemetry,
    });
    persistActiveSession({ ...persistedSession, updatedAt: Date.now() });
  };

  let nextChunk = 0;
  const workers = Array.from({
    length: Math.min(MAX_RESUMABLE_UPLOAD_CONCURRENCY, totalChunks),
  }).map(async (_unused, workerIndex) => {
    while (nextChunk < totalChunks) {
      if (workerIndex >= activeConcurrency) {
        await wait(250);
        continue;
      }
      const chunkIndex = nextChunk;
      nextChunk += 1;
      await waitIfUploadPaused(session.uploadId);
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, options.file.size);
      const startedAt = Date.now();
      const result = await uploadChunkWithRetry(
        session.uploadId,
        chunkIndex,
        options.file.slice(start, end),
        uploadConfig,
      );
      telemetryTracker.retryCount += Math.max(0, result.attempts - 1);
      if (
        Date.now() - startedAt < 8_000 &&
        activeConcurrency < MAX_RESUMABLE_UPLOAD_CONCURRENCY
      ) {
        activeConcurrency += 1;
      }
      uploadedChunks.add(chunkIndex);
      reportProgress();
    }
  });
  await Promise.all(workers);

  await ensureMissingChunksUploaded({
    uploadId: session.uploadId,
    file: options.file,
    chunkSize,
    totalChunks,
    onProgress: options.onProgress,
    requestConfig: uploadConfig,
  });

  return runResumableUploadWithMismatchHandling(
    async (allowPatientMismatchOverride) =>
      finalizeResumableUpload({
        uploadId: session.uploadId,
        allowPatientMismatchOverride,
        onProgress: options.onProgress,
      }),
    options,
  );
}

export async function uploadEegSessionFileResumable(
  options: ResumableSessionFileUploadOptions,
): Promise<StagedSessionFileUpload> {
  const identity = await hashEegUploadHeader(options.file);
  options.onProgress?.(1);
  const uploadConfig = await buildEegUploadRequestConfig(identity.headerSha256);
  const initResponse = await apiClient.post<{
    data: ResumableUploadInitPayload;
  }>(
    "/eeg/upload/init",
    {
      study_id: options.studyId,
      filename: options.relativePath || options.file.name,
      filesize: options.file.size,
      size_bytes: options.file.size,
      header_sha256: identity.headerSha256,
      chunk_size_bytes: chooseRequestedChunkSize(options.file.size),
      owning_team_id: options.owningTeamId || undefined,
      defer_recording_finalization: "true",
    },
    uploadConfig,
  );

  const session = initResponse.data?.data;
  if (!session?.uploadId) {
    throw new Error("Session file upload did not return a valid upload session.");
  }
  if (shouldSkipEegChunkUpload(session)) {
    options.onProgress?.(100);
    return {
      staged_upload_id: session.staged_upload_id || session.uploadId,
      filename: session.filename || options.file.name,
      size_bytes: session.size_bytes || options.file.size,
      fieldname: options.fieldname,
      relative_path: options.relativePath || options.file.name,
    };
  }
  if (!session.chunkSize || !session.totalChunks) {
    throw new Error("Session file upload did not return a valid upload session.");
  }

  const persistedSession: PersistedResumableEegUploadSession = {
    uploadId: session.uploadId,
    recordingId: session.recordingId,
    filename: options.relativePath || options.file.name,
    filesize: options.file.size,
    studyId: options.studyId,
    chunkSize: session.chunkSize,
    totalChunks: session.totalChunks,
    owningTeamId: options.owningTeamId || null,
    importTaskId: options.importTaskId,
    startedAt: Date.now(),
    updatedAt: Date.now(),
  };
  persistActiveSession(persistedSession);

  const uploadedChunks = new Set<number>();
  let nextChunk = 0;
  const telemetryTracker = createTelemetryTracker();
  let activeConcurrency = Math.min(
    MIN_RESUMABLE_UPLOAD_CONCURRENCY,
    session.totalChunks,
  );
  const reportProgress = () => {
    options.onProgress?.(
      Math.min(
        99,
        Math.round((uploadedChunks.size * 100) / Math.max(session.totalChunks, 1)),
      ),
    );
    emitUploadTelemetry({
      tracker: telemetryTracker,
      uploadedChunks: uploadedChunks.size,
      totalChunks: session.totalChunks,
      chunkSize: session.chunkSize,
      totalBytes: options.file.size,
      activeConcurrency,
      onTelemetry: options.onTelemetry,
    });
    persistActiveSession({ ...persistedSession, updatedAt: Date.now() });
  };

  const workers = Array.from({
    length: Math.min(MAX_RESUMABLE_UPLOAD_CONCURRENCY, session.totalChunks),
  }).map(async (_unused, workerIndex) => {
    while (nextChunk < session.totalChunks) {
      if (workerIndex >= activeConcurrency) {
        await wait(250);
        continue;
      }
      const chunkIndex = nextChunk;
      nextChunk += 1;
      await waitIfUploadPaused(session.uploadId);
      const start = chunkIndex * session.chunkSize;
      const end = Math.min(start + session.chunkSize, options.file.size);
      const startedAt = Date.now();
      const result = await uploadChunkWithRetry(
        session.uploadId,
        chunkIndex,
        options.file.slice(start, end),
        uploadConfig,
      );
      telemetryTracker.retryCount += Math.max(0, result.attempts - 1);
      if (
        Date.now() - startedAt < 8_000 &&
        activeConcurrency < MAX_RESUMABLE_UPLOAD_CONCURRENCY
      ) {
        activeConcurrency += 1;
      }
      uploadedChunks.add(chunkIndex);
      reportProgress();
    }
  });
  await Promise.all(workers);

  await ensureMissingChunksUploaded({
    uploadId: session.uploadId,
    file: options.file,
    chunkSize: session.chunkSize,
    totalChunks: session.totalChunks,
    onProgress: options.onProgress,
    requestConfig: uploadConfig,
  });

  const completeResponse = await apiClient.post<{
    data: {
      staged_upload_id: string;
      filename: string;
      size_bytes: number;
      status: "staged";
    };
  }>(
    `/eeg/upload/${session.uploadId}/complete`,
    {},
    uploadConfig,
  );
  const staged = completeResponse.data?.data;
  if (!staged?.staged_upload_id) {
    throw new Error("Session file upload completed without a staged upload id.");
  }

  clearPersistedSession(session.uploadId);
  options.onProgress?.(100);
  return {
    staged_upload_id: staged.staged_upload_id,
    filename: staged.filename,
    size_bytes: staged.size_bytes,
    fieldname: options.fieldname,
    relative_path: options.relativePath || options.file.name,
  };
}

export function shouldUseResumableEegUpload(fileSizeBytes: number): boolean {
  return fileSizeBytes >= DIRECT_EEG_UPLOAD_THRESHOLD_BYTES;
}

export { fileMatchesResumableSession };

export const RESUMABLE_EEG_UPLOAD_THRESHOLD_BYTES =
  DIRECT_EEG_UPLOAD_THRESHOLD_BYTES;
